import { calculateComission } from "./calculateComission";
import { refKeyDeOportunidad } from "./lineasFacturacion/refKey";

// comisionDelMes — la MISMA cuenta de la card COMISIÓN LIQUIDABLE (VentasFacturasEjecutivo),
// re-agrupada por (ejecutivo, mes de COBRO REAL). NO reimplementa reglas ni re-descuenta nada:
// getSalesCommissionBreakdown ya resuelve intermediación + edificios + take + apx + broker dentro
// de su netoFinal, y devuelve la porción por ejecutivo. Acá solo se filtra esa porción, se filtra
// por cobro real y se bucketea por mes.
//
// LIQUIDABLE = cobro REAL 100% del cliente final. getCobroReal (buildCobroPorVenta) evalúa
// MBF → factoring → cuotas y devuelve la vía.
//
// REGLA (autoritativa, Ignacio 2026-08-11 — NO reinterpretar): un FACTORING LIQUIDADO es cobro del
// cliente final. El cliente ya le pagó al factor y la operación se cerró, así que la comisión está
// RECONOCIDA y es liquidable, igual que MBF / cuotas. El factoring solo NO cuenta mientras está
// vigente (no liquidado): ahí el adelanto es financiamiento con recurso, no cobro. Esa distinción ya
// la hace getCobroReal, que devuelve via='factoring' SOLO cuando estado_factoring='liquidada'.
//
// Todo-o-nada a nivel factura → la venta aporta su comisión COMPLETA en el mes de su cobro real.
//
// deps: { oportunidades, clientes, intermediarios, edificios, snapMap, pctMap, getRate, getTake,
//         cobroPorVenta } — los mismos inputs que la card LIQUIDABLE + el Map cobroPorVenta
// (buildCobroPorVenta) para distinguir la vía. `empleadoPorUsuario` (opcional) solo lo usa
// detalleLiquidableDelMes para el puente usuario→empleado.

// Vías que cuentan como pago REAL del cliente final → reconocen comisión liquidable.
// REGLA (autoritativa): factoring LIQUIDADO = cobrado del cliente final → liquidable, igual que
// mbf/cuotas. getCobroReal ya devuelve via='factoring' SOLO cuando la operación está liquidada.
// (`fk_directa` queda como vía histórica; getCobroReal ya no la emite tras B3 Fase A.)
// `corte_historico` (Fase 3.2b, 2026-09-19): un cobro sembrado por corte histórico (pre-Clay) YA
// ocurrió en su momento → es un cobro real del cliente final, liquidable como los demás. Sin esto,
// una venta cuya emitida se salda por corte (ej. folio 27 / emitida 3199) quedaría fuera de comisión.
export const VIAS_LIQUIDABLES = new Set(["mbf", "fk_directa", "factoring", "cuotas", "corte_historico"]);

// Clave de venta por el util centralizado (10.1), misma que produce buildCobroPorVenta. `oportunidades`
// acá es SIEMPRE pauta (sales) o contrato — cartelería/hardware nunca fluye por comisionDelMes — y las
// filas de pauta llegan sin `type` seteado (autoMaterializarComisiones las arma con spread de `sales`
// crudo), así que el discriminador sigue siendo `type === "contrato"`, no un `op.type` genérico.
const keyVenta = (op) =>
  op.type === "contrato"
    ? refKeyDeOportunidad({ type: "contrato", contrato_id: op.contrato_id, mes_index: op.mes_index })
    : refKeyDeOportunidad({ type: "pauta", id: op.id });

// Núcleo compartido: aportes LIQUIDABLES (cobro real, factoring excluido) del ejecutivo en el
// (año, mes) de cobro real. Cada aporte trae la comisión del ejecutivo de esa venta.
const aportesLiquidables = (ejecutivoId, año, mes, deps) => {
  const { oportunidades, clientes, intermediarios, edificios, snapMap, pctMap, getRate, getTake, cobroPorVenta } = deps;
  const target = `${año}-${String(mes).padStart(2, "0")}`;
  const ejec = String(ejecutivoId);
  const out = [];

  for (const op of oportunidades || []) {
    if (op.estatus !== "100%" || op.eliminada) continue;

    // Verdad de cobro por venta. Liquidable SOLO si está 100% cobrada por una vía REAL de cobro del
    // cliente final: MBF / factoring liquidado / cuotas (ver VIAS_LIQUIDABLES y la regla de arriba).
    const cobro = cobroPorVenta?.get(keyVenta(op));
    if (!cobro?.cobrada || !VIAS_LIQUIDABLES.has(cobro.via)) continue;
    // Bucket por el MES del cobro real.
    if (!cobro.fecha || String(cobro.fecha).slice(0, 7) !== target) continue;

    // Idéntico a la card: convertir a moneda local y resolver el take, luego el desglose existente.
    const rate = getRate(op);
    const local = op.moneda_dolar ? { ...op, importe: (parseFloat(op.importe) || 0) * rate, moneda_dolar: false } : op;
    const desglose = calculateComission.getSalesCommissionBreakdown(
      local,
      clientes,
      intermediarios,
      edificios,
      undefined,
      snapMap,
      getTake(op),
      pctMap
    );

    const lineas = desglose.filter((d) => String(d.ejecutivo) === ejec);
    const comision = lineas.reduce((s, d) => s + (d.comision || 0), 0);
    if (!comision) continue;

    const pct = lineas[0]?.percentage ?? null;
    out.push({
      ventaId: op.id,
      clienteId: op.client_id ?? null,
      cliente: op.cliente ?? null,
      mesCierre: op.mes_cierre || null,
      fechaCobro: cobro.fecha,
      via: cobro.via,
      importeCobro: parseFloat(local.importe) || 0,
      neto: pct ? comision / (pct / 100) : null, // netoFinal derivado (comision = neto × pct/100)
      pct,
      comision,
    });
  }
  return out;
};

// @returns {{ ejecutivoId, año, mes, total, aportes:[], porCierre:{} }}
export const comisionDelMes = (ejecutivoId, año, mes, deps = {}) => {
  const aportes = aportesLiquidables(ejecutivoId, año, mes, deps);
  const total = aportes.reduce((s, a) => s + a.comision, 0);
  const porCierre = {};
  for (const a of aportes) {
    const k = a.mesCierre || "sin-cierre";
    porCierre[k] = (porCierre[k] || 0) + a.comision;
  }
  return { ejecutivoId: String(ejecutivoId), año, mes, total, aportes, porCierre };
};

// Detalle por-venta de las comisiones LIQUIDABLES (cobro real) de un (año, mes), listo para que
// 3.2b-2 lo materialice como haberes. NO escribe nada. `empleado_id` sale del puente
// usuario_id ↔ empleado (deps.empleadoPorUsuario), null si el ejecutivo no tiene empleado.
export const detalleLiquidableDelMes = (usuarioId, año, mes, deps = {}) => {
  const empleadoId = deps.empleadoPorUsuario ? (deps.empleadoPorUsuario[String(usuarioId)] ?? null) : null;
  return aportesLiquidables(usuarioId, año, mes, deps).map((a) => ({
    empleado_id: empleadoId,
    usuario_id: String(usuarioId),
    venta_id: a.ventaId,
    cliente: a.cliente,
    pct: a.pct,
    neto: a.neto,
    comision: a.comision,
    fecha_cobro_real: a.fechaCobro,
    concepto: `Comisión ${a.cliente ?? "s/cliente"} · venta ${a.ventaId} (cobro ${a.fechaCobro})`,
  }));
};
